import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Clock, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, EVENT_TYPES, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const statusStyle = {
  publie: "hidden",
  complet: "bg-[#C8102E]/20 text-[#ff6b81] border-[#C8102E]/40",
  annule: "bg-gray-700/40 text-gray-400 border-gray-600",
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const favorites = (user && user !== false && user.favorite_event_ids) || [];

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const toggleFavorite = async (eventId) => {
    if (!user || user === false) {
      toast.info("Connectez-vous pour enregistrer vos soirées favorites");
      navigate("/connexion");
      return;
    }
    try {
      const { data } = await api.post(`/favorites/${eventId}`);
      setUser({ ...user, favorite_event_ids: data.favorite_event_ids });
      toast.success(data.favorite_event_ids.includes(eventId) ? "Ajoutée à vos favoris ❤️" : "Retirée de vos favoris");
    } catch {
      toast.error("Erreur, réessayez");
    }
  };

  const types = ["Tous", ...EVENT_TYPES.filter((t) => events.some((e) => e.event_type === t))];
  const filtered = filter === "Tous" ? events : events.filter((e) => e.event_type === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="events-page">
      <p className="gold-label mb-3">Live music · Karaoké · Concerts · DJ</p>
      <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold mb-10">Programme des soirées</h1>
      <div className="flex gap-2 overflow-x-auto pb-4 mb-10">
        {types.map((t) => (
          <button
            key={t}
            data-testid={`event-filter-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
            onClick={() => setFilter(t)}
            className={`whitespace-nowrap px-5 py-2 rounded-full text-sm border transition-colors duration-200 ${
              filter === t ? "bg-[#D4AF37] text-black border-[#D4AF37] font-medium" : "border-white/15 text-gray-300 hover:border-[#D4AF37]/50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
        {filtered.map((e) => (
          <div key={e.id} className={`ic-card overflow-hidden flex flex-row md:flex-col ${e.status === "annule" ? "opacity-60" : ""}`} data-testid={`event-card-${e.id}`}>
            {e.photo_url && (
              <div className="relative w-[40%] shrink-0 md:w-full">
                <a href={e.photo_url} target="_blank" rel="noopener noreferrer" data-testid={`event-poster-${e.id}`}>
                  <img src={e.photo_url} alt={e.artist_name} className="w-full h-full md:h-auto md:aspect-[9/16] object-cover bg-black" />
                </a>
                <button
                  data-testid={`event-favorite-${e.id}`}
                  onClick={() => toggleFavorite(e.id)}
                  className="absolute top-2 left-2 md:top-3 md:left-3 p-2 rounded-full bg-black/60 backdrop-blur border border-white/10 transition-transform duration-200 active:scale-90"
                  title="Ajouter aux favoris"
                >
                  <Heart size={17} className={favorites.includes(e.id) ? "text-[#C8102E] fill-[#C8102E]" : "text-white"} />
                </button>
                {e.status !== "publie" && (
                  <span className={`absolute top-2 right-2 md:top-3 md:right-3 text-xs border rounded-full px-3 py-1 backdrop-blur ${statusStyle[e.status] || ""}`}>
                    {STATUS_LABELS[e.status]}
                  </span>
                )}
              </div>
            )}
            <div className="p-4 md:p-6 flex flex-col flex-1 min-w-0">
              <p className="gold-label">{e.event_type}</p>
              <h2 className="font-serif-elegant text-lg md:text-2xl font-semibold mt-1 md:mt-2 leading-snug">{e.title}</h2>
              <p className="text-sm text-[#D4AF37] mt-1">{e.artist_name}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 mt-2 md:mt-3">
                <span className="flex items-center gap-1.5"><CalendarDays size={14} />{new Date(e.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
                <span className="flex items-center gap-1.5"><Clock size={14} />{e.time}</span>
              </div>
              <p className="text-sm text-gray-400 mt-2 md:mt-3 leading-relaxed flex-1 line-clamp-3 md:line-clamp-none">{e.description}</p>
              {e.status === "annule" && e.cancellation_message && (
                <p className="text-xs text-[#ff6b81] mt-3">{e.cancellation_message}</p>
              )}
              {e.status === "publie" && (
                <Link to={`/reserver?event=${e.id}`} data-testid={`event-reserve-${e.id}`} className="btn-gold !py-2 text-sm mt-4 md:mt-5 self-start">
                  Réserver
                </Link>
              )}
              {e.status === "complet" && <p className="text-sm text-[#ff6b81] mt-5">Soirée complète</p>}
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-gray-400">Aucune soirée programmée pour le moment.</p>}
    </div>
  );
}
